import { Box, Button, Tab, Tabs } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveDiagramPageData,
  selectDiagramActiveDepartment,
  selectDiagramActivePage,
} from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import { DIAGRAM_DEPARTMENT_TABS, DIAGRAM_PAGE_COUNT } from "../data";
import { DiagramDepartmentTab } from "../types";
import { FloorAndStraps } from "./FloorAndStraps";
import { getTextDirection } from "./InfoCard";
import { SpecDiagram } from "./SpecDiagram";
import { LightingElectronics } from "./LightingElectronics";
import { ToolsAndNotes } from "./ToolsAndNotes";
import { useStyles } from "./styles";

const DiagramView = () => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const textDirection = getTextDirection(i18n.language);
  const isHebrew = textDirection === "rtl";
  const activeDepartment = useSelector(selectDiagramActiveDepartment);
  const activePage = useSelector(selectDiagramActivePage);
  const pageData = useSelector(selectActiveDiagramPageData);

  const departmentTabsToRender = useMemo(
    () =>
      isHebrew
        ? [...DIAGRAM_DEPARTMENT_TABS].reverse()
        : DIAGRAM_DEPARTMENT_TABS,
    [isHebrew]
  );
  const pageIndexesToRender = useMemo(
    () =>
      isHebrew
        ? Array.from(
            { length: DIAGRAM_PAGE_COUNT },
            (_, index) => DIAGRAM_PAGE_COUNT - 1 - index
          )
        : Array.from({ length: DIAGRAM_PAGE_COUNT }, (_, index) => index),
    [isHebrew]
  );

  if (!pageData) {
    return null;
  }

  const renderPageContent = () => {
    switch (pageData.layout) {
      case "sections":
        return <FloorAndStraps pageData={pageData} />;
      case "toolsAndNotes":
        return <ToolsAndNotes pageData={pageData} />;
      case "singleSection":
        return <LightingElectronics pageData={pageData} />;
      case "overview":
      default:
        return <SpecDiagram pageData={pageData} />;
    }
  };

  return (
    <Box className={styles.pageWrapper}>
      <Box className={styles.layoutFrame} dir={textDirection}>
        <Box className={styles.departmentTabsRow} dir="ltr">
          <Box className={isHebrew ? styles.departmentTabsWrapper : styles.departmentTabsWrapperEn}>
            <Tabs
              className={isHebrew ? styles.departmentTabs : styles.departmentTabsEn}
              value={activeDepartment}
              onChange={(_, nextValue) =>
                dispatch(actions.setDiagramDepartment(nextValue as DiagramDepartmentTab))
              }
              variant={isHebrew ? "fullWidth" : "scrollable"}
              scrollButtons={false}
            >
              {departmentTabsToRender.map((tab) => (
                <Tab
                  key={tab.id}
                  label={t(tab.labelKey)}
                  value={
                    DiagramDepartmentTab[
                      tab.id as keyof typeof DiagramDepartmentTab
                    ]
                  }
                />
              ))}
            </Tabs>
          </Box>
        </Box>

        <Box className={styles.toolbar} dir="ltr">
          <Button
            className={styles.exportButton}
            variant="contained"
            color="primary"
            onClick={() => dispatch(actions.exportDiagramPdf())}
          >
            {t("WORK_ORDER.DIAGRAM.EXPORT_PDF")}
          </Button>

          <Tabs
            className={styles.pageTabs}
            value={activePage}
            onChange={(_, nextValue) =>
              dispatch(actions.setDiagramPage(nextValue as number))
            }
            variant="scrollable"
            scrollButtons={false}
          >
            {pageIndexesToRender.map((index) => (
              <Tab
                key={index}
                label={
                  <Box className={styles.pageTabLabel}>
                    {textDirection === "rtl" ? (
                      <>
                        <span>{index + 1}</span>
                        <span>{t("WORK_ORDER.DIAGRAM.PAGE_LABEL")}</span>
                      </>
                    ) : (
                      <>
                        <span>{t("WORK_ORDER.DIAGRAM.PAGE_LABEL")}</span>
                        <span>{index + 1}</span>
                      </>
                    )}
                  </Box>
                }
                value={index}
              />
            ))}
          </Tabs>
        </Box>

        <Box className={styles.contentContainer} dir={textDirection}>
          {renderPageContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default DiagramView;
